import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { portalAPI } from '@/lib/api/portal';
import { formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useAvailabilityMonitor, useUserRegistrations } from '@/hooks';
import { Calendar, Clock, Users, MapPin, Swords } from 'lucide-react';
import type { Tournament } from '@/types';

export const Route = createFileRoute('/tournaments')({
  component: TournamentsPage,
});

function TournamentCard({ tournament }: { tournament: Tournament }) {
  const { spotsRemaining, isAvailable } = useAvailabilityMonitor('tournament', tournament.id);
  const { registeredTournamentIds } = useUserRegistrations();
  const isRegistered = registeredTournamentIds.includes(tournament.id);

  // Check early bird pricing
  const now = new Date();
  const isEarlyBird =
    tournament.earlyBirdPrice &&
    tournament.earlyBirdStartDate &&
    tournament.earlyBirdEndDate &&
    now >= new Date(tournament.earlyBirdStartDate) &&
    now <= new Date(tournament.earlyBirdEndDate);

  const price = isEarlyBird ? tournament.earlyBirdPrice! : tournament.registrationFee;

  const getStatusBadge = () => {
    if (isRegistered) return <Badge variant="success">Registered</Badge>;
    if (tournament.status === 'full' || !isAvailable) return <Badge variant="destructive">Full</Badge>;
    if (tournament.status === 'closed') return <Badge variant="secondary">Closed</Badge>;
    if (tournament.status === 'open') return <Badge variant="default">Open</Badge>;
    return null;
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-xl">{tournament.name}</CardTitle>
            <CardDescription className="flex items-center gap-2">
              {tournament.weapon && (
                <span className="capitalize flex items-center gap-1">
                  <Swords className="h-4 w-4" />
                  {tournament.weapon}
                </span>
              )}
              {tournament.division && (
                <Badge variant="outline" className="capitalize">
                  {tournament.division}
                </Badge>
              )}
            </CardDescription>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {tournament.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{tournament.description}</p>
        )}

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(tournament.date)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{formatTime(tournament.startTime)}</span>
            {tournament.endTime && <span>- {formatTime(tournament.endTime)}</span>}
          </div>
          {tournament.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{tournament.location}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>
              {spotsRemaining !== null
                ? `${spotsRemaining} spots remaining`
                : `${tournament.maxParticipants} max participants`}
            </span>
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">{formatCurrency(price)}</span>
            {isEarlyBird && (
              <span className="text-sm text-muted-foreground line-through">
                {formatCurrency(tournament.registrationFee)}
              </span>
            )}
          </div>
          {isEarlyBird && (
            <Badge variant="success" className="mt-1">
              Early Bird Pricing
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter>
        {isRegistered ? (
          <Button variant="secondary" className="w-full" disabled>
            Already Registered
          </Button>
        ) : (
          <Link to="/register" className="w-full">
            <Button className="w-full" disabled={!isAvailable || tournament.status !== 'open'}>
              Register Now
            </Button>
          </Link>
        )}
      </CardFooter>
    </Card>
  );
}

function TournamentsPage() {
  const { data: tournaments, isLoading, error } = useQuery({
    queryKey: ['tournaments', { visible: true }],
    queryFn: () => portalAPI.getTournaments({ visible: true }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-destructive">Failed to load tournaments</p>
      </div>
    );
  }

  const tournamentDocs = tournaments?.docs ?? [];
  const openTournaments = tournamentDocs.filter((t) => t.status === 'open');
  const otherTournaments = tournamentDocs.filter((t) => t.status !== 'open');

  return (
    <div className="container py-8 space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-heading font-bold">Tournaments</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Browse and register for competitive tournaments
        </p>
      </div>

      {openTournaments.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-heading font-semibold">Open for Registration</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {openTournaments.map((tournament) => (
              <TournamentCard key={tournament.id} tournament={tournament} />
            ))}
          </div>
        </section>
      )}

      {otherTournaments.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-heading font-semibold text-muted-foreground">
            Other Tournaments
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
            {otherTournaments.map((tournament) => (
              <TournamentCard key={tournament.id} tournament={tournament} />
            ))}
          </div>
        </section>
      )}

      {tournamentDocs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tournaments available at this time</p>
        </div>
      )}
    </div>
  );
}
